import { isShapeObjectType } from "@bezpart-board/shared";
import { hitTestAllObjects } from "../HitTest";
import type { SceneHost, SceneObject } from "../SceneHost";
import { getEraserSettings, subscribeEraserSettings } from "../eraserSession";
import { isBoardEditorChrome, isBoardWidgetChromeExceptCalcPlot } from "./boardWidgetChrome";

const DRAG_THRESHOLD_PX = 4;
const ERASER_PENDING_CLASS = "is-eraser-pending";

/**
 * Фигуры и рамки ластиком не стираются.
 *
 * Ластик удаляет объект целиком, а не кусок под кругом. Для росчерка пера
 * это естественно, а прямоугольник или рамка исчезали целиком от касания
 * края — человек стирал подпись рядом и терял всё построение.
 */
function isEraserShapeProtected(obj: SceneObject): boolean {
  return isShapeObjectType(obj.type) || obj.type === "frame";
}

/** Текст пропускаем молча; медиа — с подсказкой. */
function isEraserMediaProtected(obj: SceneObject): boolean {
  return (
    obj.type === "pdf_document" ||
    obj.type === "image" ||
    obj.type === "calculator" ||
    obj.type === "timer" ||
    obj.type === "code" ||
    obj.type === "sql" ||
    obj.type === "audio"
  );
}

export type EraserToolBinding = {
  destroy: () => void;
};

export function bindEraserTool(host: SceneHost): EraserToolBinding {
  let session: {
    startX: number;
    startY: number;
    dragging: boolean;
    erased: Set<string>;
    lockedSkipped: boolean;
    mediaSkipped: boolean;
    shapeSkipped: boolean;
  } | null = null;

  function noteLockedSkip() {
    if (session) session.lockedSkipped = true;
  }

  function noteMediaSkip() {
    if (session) session.mediaSkipped = true;
  }

  function noteShapeSkip() {
    if (session) session.shapeSkipped = true;
  }

  function eraserRadius() {
    return getEraserSettings().radius;
  }

  /* ─── круг под указателем: видно, что именно сотрётся ─── */

  const ring = document.createElement("div");
  ring.className = "wb-eraser-ring";
  ring.setAttribute("aria-hidden", "true");
  ring.style.display = "none";
  document.body.appendChild(ring);

  /** Сколько экранных пикселей в одной единице доски. */
  function pxPerWorld(): number {
    const a = host.stagePoint(0, 0);
    const b = host.stagePoint(1, 0);
    const worldPerPx = Math.abs(b.x - a.x);
    return Number.isFinite(worldPerPx) && worldPerPx > 0 ? 1 / worldPerPx : 1;
  }

  function moveRing(clientX: number, clientY: number) {
    const d = eraserRadius() * 2 * pxPerWorld();
    ring.style.width = `${d}px`;
    ring.style.height = `${d}px`;
    ring.style.left = `${clientX}px`;
    ring.style.top = `${clientY}px`;
    ring.style.display = "";
  }

  function hideRing() {
    ring.style.display = "none";
  }

  function onHoverMove(ev: PointerEvent) {
    if (host.getActiveTool() !== "eraser") {
      hideRing();
      return;
    }
    moveRing(ev.clientX, ev.clientY);
  }

  const unsubscribeSettings = subscribeEraserSettings(() => {
    if (ring.style.display === "none") return;
    const d = eraserRadius() * 2 * pxPerWorld();
    ring.style.width = `${d}px`;
    ring.style.height = `${d}px`;
  });

  function markPendingVisual(id: string) {
    const el = host.getObjectElement(id);
    if (el) el.classList.add(ERASER_PENDING_CLASS);
  }

  function eraseObjectId(id: string) {
    if (!session || session.erased.has(id)) return;
    session.erased.add(id);
    markPendingVisual(id);
    void host.deleteObject(id);
  }

  function collectAndEraseAt(clientX: number, clientY: number) {
    if (!session) return;
    const pt = host.stagePoint(clientX, clientY);
    const r = eraserRadius();
    for (const obj of hitTestAllObjects(host.getObjects(), pt.x, pt.y, r)) {
      if (obj.type === "text") continue;
      if (isEraserShapeProtected(obj)) {
        noteShapeSkip();
        continue;
      }
      if (isEraserMediaProtected(obj)) {
        noteMediaSkip();
        continue;
      }
      if (obj.locked) {
        noteLockedSkip();
        continue;
      }
      eraseObjectId(obj.id);
    }
  }

  function onPointerDown(ev: PointerEvent) {
    if (!host.canEdit || host.getActiveTool() !== "eraser") return;
    if (host.isInteractionBlocked()) return;
    if (isBoardEditorChrome(ev.target)) return;
    if (isBoardWidgetChromeExceptCalcPlot(ev.target)) return;
    // У пера графического планшета обратный конец приходит как button 5
    // («ластик» в Pointer Events), а не 0 — из-за строгой проверки на 0
    // ластик на планшете просто молчал. Для мыши проверка остаётся.
    if (ev.pointerType === "mouse" && ev.button !== 0) return;
    ev.preventDefault();
    ev.stopPropagation();

    // Захват указателя: у пера события иначе теряются, если оно на миг
    // отрывается от планшета, и стирание «залипает» на полпути.
    try {
      host.hitSurface.setPointerCapture?.(ev.pointerId);
    } catch {
      /* не критично */
    }

    session = {
      startX: ev.clientX,
      startY: ev.clientY,
      dragging: false,
      erased: new Set(),
      lockedSkipped: false,
      mediaSkipped: false,
      shapeSkipped: false,
    };

    // Тычок без движения стирает ВСЁ под кругом, а не только верхний объект.
    // Иначе размер ластика ничего не значил: человек брал большой, накрывал
    // им две линии разом, а уходила одна. Круг — это и есть заявленная
    // площадь; лишнее возвращается отменой.
    collectAndEraseAt(ev.clientX, ev.clientY);

    document.addEventListener("pointermove", onPointerMove);
    document.addEventListener("pointerup", onPointerUp);
    document.addEventListener("pointercancel", onPointerUp);
  }

  function onPointerMove(ev: PointerEvent) {
    moveRing(ev.clientX, ev.clientY);
    if (!session) return;
    if (!session.dragging) {
      if (
        Math.hypot(ev.clientX - session.startX, ev.clientY - session.startY) <
        DRAG_THRESHOLD_PX
      ) {
        return;
      }
      session.dragging = true;
      collectAndEraseAt(session.startX, session.startY);
    }
    collectAndEraseAt(ev.clientX, ev.clientY);
  }

  function finish(commit: boolean) {
    if (!session) return;
    const state = session;
    session = null;
    document.removeEventListener("pointermove", onPointerMove);
    document.removeEventListener("pointerup", onPointerUp);
    document.removeEventListener("pointercancel", onPointerUp);

    if (!commit) return;

    if (state.shapeSkipped) {
      host.notify("Фигуры ластиком не стираются — выделите и нажмите «удал»");
    } else if (state.mediaSkipped) {
      host.notify("Презентации и изображения нельзя стереть ластиком");
    } else if (state.lockedSkipped) {
      host.notify("Заблокированные объекты нельзя стереть");
    }
  }

  function onPointerUp(ev: PointerEvent) {
    finish(true);
    void ev;
  }

  host.hitSurface.addEventListener("pointerdown", onPointerDown);
  host.hitSurface.addEventListener("pointermove", onHoverMove);
  host.hitSurface.addEventListener("pointerleave", hideRing);
  window.addEventListener("wb:live-viewport", hideRing);

  return {
    destroy() {
      finish(false);
      host.hitSurface.removeEventListener("pointerdown", onPointerDown);
      host.hitSurface.removeEventListener("pointermove", onHoverMove);
      host.hitSurface.removeEventListener("pointerleave", hideRing);
      window.removeEventListener("wb:live-viewport", hideRing);
      unsubscribeSettings();
      ring.remove();
    },
  };
}
